import type { ArticleSection } from '../../../mocks/blog';
import { useTheme } from '../../../hooks/useTheme';

interface ArticleContentProps {
  sections: ArticleSection[];
}

export default function ArticleContent({ sections }: ArticleContentProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  return (
    <div className="space-y-5">
      {sections.map((section, idx) => {
        if (section.type === 'heading2') {
          return (
            <h2
              key={idx}
              className={`font-heading text-xl sm:text-2xl font-bold mt-8 mb-2 ${isLight ? 'text-navy-950' : 'text-white'}`}
            >
              {section.content}
            </h2>
          );
        }

        if (section.type === 'heading3') {
          return (
            <h3
              key={idx}
              className={`font-heading text-lg font-bold mt-6 mb-2 ${isLight ? 'text-navy-900' : 'text-white/90'}`}
            >
              {section.content}
            </h3>
          );
        }

        if (section.type === 'paragraph') {
          return (
            <p
              key={idx}
              className={`font-body text-sm sm:text-base leading-relaxed ${isLight ? 'text-navy-700' : 'text-white/70'}`}
            >
              {section.content}
            </p>
          );
        }

        if (section.type === 'list' && section.items) {
          return (
            <ul key={idx} className="space-y-2.5 pl-1">
              {section.items.map((item, iIdx) => (
                <li key={iIdx} className="flex items-start gap-3">
                  <div
                    className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: '#00cfff' }}
                  />
                  <span className={`font-body text-sm sm:text-base leading-relaxed ${isLight ? 'text-navy-700' : 'text-white/65'}`}>
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          );
        }

        if (section.type === 'quote') {
          return (
            <blockquote
              key={idx}
              className="relative pl-5 py-4 rounded-r-xl my-6"
              style={{
                borderLeft: '3px solid #00cfff',
                backgroundColor: isLight ? '#f0fdff' : 'rgba(0,207,255,0.06)',
              }}
            >
              <div className="w-4 h-4 flex items-center justify-center absolute top-4 left-4 opacity-30" style={{ color: '#00cfff' }}>
                <i className="ri-double-quotes-l text-sm" />
              </div>
              <p className={`font-body text-sm sm:text-base italic leading-relaxed pl-5 ${isLight ? 'text-navy-700' : 'text-white/70'}`}>
                {section.content}
              </p>
            </blockquote>
          );
        }

        if (section.type === 'highlight') {
          return (
            <div
              key={idx}
              className="flex gap-3 p-4 sm:p-5 rounded-xl my-4"
              style={{
                backgroundColor: isLight ? '#fffbeb' : 'rgba(249,115,22,0.08)',
                border: '1px solid rgba(249,115,22,0.25)',
              }}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5" style={{ color: '#f97316' }}>
                <i className="ri-lightbulb-line text-base" />
              </div>
              <p className={`font-body text-sm leading-relaxed ${isLight ? 'text-amber-900' : 'text-white/75'}`}>
                {section.content}
              </p>
            </div>
          );
        }

        if (section.type === 'divider') {
          return (
            <hr
              key={idx}
              className="my-6"
              style={{ borderColor: isLight ? 'rgba(10,22,40,0.08)' : 'rgba(255,255,255,0.08)' }}
            />
          );
        }

        return null;
      })}
    </div>
  );
}
